import { mean } from '@muzly/voice-engine';

import { overallScore } from './record';

import type { DiagnosisRecord } from './record';

/**
 * 목소리 피로도 — **컨디션별로 내 점수가 어떻게 달랐나**.
 *
 * 축 넷은 설문의 `condition` 네 선택지 그대로다(최상·보통·살짝 잠김·많이 잠김). 축 값은
 * 그 컨디션이라고 답한 회차들의 종합 점수 평균이다. 그러니까 이 레이더는 한 회차의 사진이
 * 아니라 **이력의 모양**이다 — 컨디션이 좋은 날과 나쁜 날의 차이가 곧 "얼마나 쉽게
 * 지치나"다.
 *
 * 임상 지표(jitter·shimmer·HNR)로 채우지 않는다. 그건 주기성을 재는 자라 허스키하거나
 * 기식음이 섞인 목소리는 정의상 낮게 나오는데, 그건 피로가 아니라 음색이다
 * (`vault: aesthetic-vs-clinical-voice-quality`). 건강한 허스키 보컬을 "피로하다"고
 * 말하는 화면을 만들지 않는다.
 *
 * **한 회차로는 성립하지 않는다.** 레이더는 비교가 본질이고 점 하나는 비교가 아니다 —
 * 축이 둘 미만이면 탭 자체가 안 선다(`visibleTabs`). 이 한계가 KB 선례에서 이 기능이
 * 기각된 이유였고(`muzly-app-diagnosis-metrics-architecture` §8.3), 이력이 쌓이는
 * 지금은 조건이 달라졌지만 **조건이 달라졌을 때만** 선다는 사실은 그대로다.
 *
 * 값의 단위는 `overallScore`라 그 함수의 미보정 한계를 그대로 물려받는다
 * (`kb: muzly-app-score-calculation-unweighted` — 측정된 축의 단순 평균).
 *
 * React·저장소 IO 의존 없음 — vitest 대상.
 */

/**
 * 축 순서 = `RadarChart`의 각도 순서(위·오른쪽·아래·왼쪽)이자 시안 §8.3의 순서다.
 * 설문(`survey.ts`의 `condition`)과 **같은 id**를 쓴다 — 어휘가 갈리면 설문에서 고른 말과
 * 결과에서 읽는 말이 달라진다.
 */
export const FATIGUE_AXIS_IDS = ['best', 'normal', 'husky', 'bad'] as const;

export type FatigueAxisId = (typeof FATIGUE_AXIS_IDS)[number];

/**
 * 한 축이 최근 몇 회차까지 보나. 컨디션은 계절과 습관을 타므로 1년 전 컨디션 좋던 날을
 * 오늘 것과 같은 무게로 평균하면 최근의 변화가 묻힌다.
 */
const WINDOW = 10;

export interface FatigueAxis {
  id: FatigueAxisId;
  /** 0..100. null = 이 컨디션으로 잰 회차가 없다. */
  value: number | null;
  /** 이 값이 몇 회차에서 나왔나. 1회짜리 축과 8회짜리 축을 같은 무게로 읽지 않게 한다. */
  takes: number;
}

const conditionOf = (record: DiagnosisRecord): string | undefined => {
  const value = record.survey.condition;
  return typeof value === 'string' ? value : undefined;
};

/**
 * 이력 배열 하나당 한 번만 센다. 결과 화면 한 번 그리는 데 탭 판정·탭 머리 점수·축 줄이
 * 같은 이력으로 각각 부른다 — 부르는 쪽마다 메모하는 대신 여기서 배열 정체성으로 묶는다
 * (이력이 바뀌면 `useDiagnoses`가 새 배열로 읽어 오므로 옛 값이 남지 않는다).
 */
const radarCache = new WeakMap<readonly DiagnosisRecord[], FatigueAxis[]>();

/** 컨디션별 축 값 — 축 넷을 **항상** 돌려준다(값 없는 축은 `value`가 null). */
export const conditionRadar = (
  records: readonly DiagnosisRecord[],
): FatigueAxis[] => {
  const cached = radarCache.get(records);
  if (cached) {
    return cached;
  }
  const axes = countRadar(records);
  radarCache.set(records, axes);
  return axes;
};

const countRadar = (records: readonly DiagnosisRecord[]): FatigueAxis[] => {
  // 최신순으로 한 번만 세워 두고 축마다 앞에서 잘라 쓴다.
  const recent = [...records].sort((a, b) => b.completedAt - a.completedAt);

  return FATIGUE_AXIS_IDS.map((id) => {
    const scores = recent
      .filter((record) => conditionOf(record) === id)
      // 컨디션은 답했는데 잰 축이 하나도 없는 회차는 평균에 넣을 숫자가 없다.
      .flatMap((record) => {
        const overall = overallScore(record);
        return overall === null ? [] : [overall];
      })
      .slice(0, WINDOW);

    return {
      id,
      value: scores.length === 0 ? null : Math.round(mean(scores)),
      takes: scores.length,
    };
  });
};

/** 값이 있는 축의 수 — 탭을 세울지 정하는 판정(`visibleTabs`)이 본다. */
export const filledAxisCount = (axes: readonly FatigueAxis[]): number =>
  axes.filter((axis) => axis.value !== null).length;

/**
 * 이 회차가 답한 컨디션의 축 값 — 탭 머리의 큰 점수이자 홈 타일의 값이다. **같은 함수**를
 * 둘이 불러야 두 화면이 같은 숫자를 말한다.
 *
 * 이 회차의 종합 점수가 아니라 **그 컨디션의 평균**인 이유: 이 탭이 답하는 질문이
 * "오늘 몇 점이었나"가 아니라 "이런 컨디션일 때 나는 어떤가"이기 때문이다.
 */
export const conditionScore = (
  record: DiagnosisRecord,
  history: readonly DiagnosisRecord[],
): number | null => {
  const condition = conditionOf(record);
  if (condition === undefined) {
    return null;
  }
  return (
    conditionRadar(history).find((axis) => axis.id === condition)?.value ?? null
  );
};
